import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  AppWindow,
  Box,
  CheckCircle2,
  ChevronRight,
  Copy,
  Cpu,
  Download,
  Eraser,
  Fingerprint,
  Info,
  Layers,
  Loader2,
  Network,
  Package,
  Play,
  PlayCircle,
  Power,
  PowerOff,
  RefreshCw,
  Search,
  Send,
  Settings as SettingsIcon,
  ShieldCheck,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import clsx from 'clsx';

import { adbApi, filesApi } from '@/lib/api';
import { useDevice } from '@/store/device';
import { formatBytes, tsLocal } from '@/lib/format';
import { IconPickerModal } from '@/components/apps/IconPickerModal';
import { staticApkApi } from '@/lib/static-apk/api';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type Scope = 'thirdParty' | 'system' | 'all';
type SortKey = 'label' | 'package' | 'version';

interface AppEntry {
  package: string;
  apkPath?: string;
  versionCode?: number;
  isSystem: boolean;
}

interface AppIconResult {
  label?: string;
  labelZh?: string;
  iconPng?: string;
  versionName?: string;
  cached: boolean;
}

interface AppInfoFlags {
  debuggable?: boolean;
  allowBackup?: boolean;
  testOnly?: boolean;
  system?: boolean;
  persistent?: boolean;
}

interface AppInfoResult {
  packageName?: string;
  versionName?: string;
  versionCode?: number;
  installerPackageName?: string;
  firstInstallTime?: string;
  lastUpdateTime?: string;
  primaryCpuAbi?: string;
  secondaryCpuAbi?: string;
  targetSdk?: number;
  minSdk?: number;
  uid?: number;
  sharedUserId?: string;
  dataDir?: string;
  codePath?: string;
  nativeLibraryDir?: string;
  installInitiatingPackageName?: string;
  installOriginatingPackageName?: string;
  isUpdatedSystemApp?: boolean;
  flags?: AppInfoFlags;
  enabled?: string;
  raw?: Record<string, string>;
}

interface AppBundleResult {
  cached: boolean;
  apkSha1?: string;
  apkSha256?: string;
  apkMd5?: string;
  apkBytes?: number;
  manifest?: {
    compileSdkVersion?: number;
    compileSdkVersionCodename?: string;
    declaredPermissions?: string[];
    nativeAbis?: string[];
    launcherActivity?: string;
    versionName?: string;
  };
  signing?: {
    schemes: Array<'v1' | 'v2' | 'v3' | 'v4'>;
    certificateSha1?: string;
    certificateSha256?: string;
    certificateMd5?: string;
    subject?: string;
    issuer?: string;
    isDebugLike?: boolean;
  };
  signingMissing?: boolean;
  signingDiagnostics?: {
    source: 'config' | 'env' | 'path' | 'sdk' | 'cached' | 'none';
    resolvedPath?: string;
    triedPaths: string[];
    sdkRoots: string[];
  };
}

interface AppComponentEntry {
  name: string;
  exported: boolean;
  intentFilters: string[];
}

interface AppProviderEntry extends AppComponentEntry {
  authorities: string[];
}

interface AppComponentsResult {
  activities: AppComponentEntry[];
  services: AppComponentEntry[];
  receivers: AppComponentEntry[];
  providers: AppProviderEntry[];
}

interface AppUserEntry {
  userId: number;
  userName: string;
  userType: 'primary' | 'secondary' | 'work' | 'guest' | 'twin' | 'other';
  running?: boolean;
  installed: boolean;
  enabled?: string;
  hidden?: boolean;
  suspended?: boolean;
  stopped?: boolean;
}

interface AppUsersResult {
  users: AppUserEntry[];
}

interface AppProcessesResult {
  foreground: boolean;
  foregroundActivity?: string;
  processes: Array<{
    processName: string;
    pid?: number;
    rssKb?: number;
    state?: string;
  }>;
}

interface NetstatsBytes {
  rxBytes: number;
  txBytes: number;
}

interface NetstatsWindow {
  windowHours: number;
  mobile?: NetstatsBytes;
  wifi?: NetstatsBytes;
  total?: NetstatsBytes;
}

interface AppNetstatsResult {
  available: boolean;
  windows: NetstatsWindow[];
  warning?: string;
}

interface AppDexoptEntry {
  isa: 'arm' | 'arm64' | 'x86' | 'x86_64' | 'unknown';
  status:
    | 'speed-profile'
    | 'speed'
    | 'verify'
    | 'quicken'
    | 'space'
    | 'extract'
    | 'run-from-apk'
    | 'unknown';
  reason?: string;
  classLoaderContext?: string;
}

interface AppDexoptResult {
  ready: boolean;
  arches: AppDexoptEntry[];
  rawSnippet?: string;
}

interface PullApkResult {
  hubPath: string;
  sizeBytes: number;
  package: string;
  versionCode?: number;
}

interface AppStorageResult {
  apkBytes: number;
  dataBytes: number | null;
  cacheBytes: number | null;
  apkFiles: number;
  note?: string;
}

interface RuntimePermission {
  name: string;
  granted: boolean;
  flags: string;
}

interface AppPermissionsResult {
  requested: string[];
  install: string[];
  runtime: RuntimePermission[];
  granted: string[];
  revoked: string[];
}

interface PullApksResult {
  files: Array<{ hubPath: string; sizeBytes: number; isBase: boolean; name: string }>;
  totalBytes: number;
  dirHubPath: string;
  package: string;
  versionCode?: number;
}

type DrawerTab = 'overview' | 'components' | 'permissions' | 'signing' | 'runtime' | 'users';

const DRAWER_TABS: ReadonlyArray<DrawerTab> = [
  'overview',
  'components',
  'permissions',
  'signing',
  'runtime',
  'users',
];

const DRAWER_TAB_LABEL: Record<DrawerTab, string> = {
  overview: '概览',
  components: '组件',
  permissions: '权限',
  signing: '签名',
  runtime: '运行',
  users: '用户',
};

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

/**
 * `/devices/:serial/apps` — single-device app management page.
 *
 * Mirrors the "App Inspection" panel you'd find in Android Studio /
 * mobile device assistants: full package list with icons + Chinese
 * app names, plus per-app actions (start / stop / clear / uninstall /
 * export APK). The list view is intentionally lazy on icons: an
 * IntersectionObserver fans out `app.icon` only when a row scrolls
 * into view, so a 200-app catalog doesn't pull 200 APKs up front.
 *
 * Bulk operations route through the existing Batch page rather than
 * reinventing the per-serial progress UI — clicking "Bulk uninstall"
 * on N selected apps navigates to `/batch?tab=app-uninstall&...` with
 * the package + selected serial pre-filled.
 */
export default function Apps() {
  const { serial, isOnline } = useDevice();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [scope, setScope] = useState<Scope>('thirdParty');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('label');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailPkg, setDetailPkg] = useState<string | undefined>();
  const [iconPickerPkg, setIconPickerPkg] = useState<AppEntry | null>(null);
  const [iconPickerLabel, setIconPickerLabel] = useState<string | undefined>();
  const [confirm, setConfirm] = useState<{
    kind: 'uninstall' | 'clear';
    pkg: string;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Pagination keeps icon-fetch fan-out bounded: 24 cards * 1 icon call
  // each = at most 24 concurrent `app.icon` requests per page view,
  // versus ~200 if we tried to render the whole catalog in one grid.
  const [pageSize, setPageSize] = useState<number>(24);
  const [page, setPage] = useState<number>(1);

  // Cache of (label, labelZh) per package, populated as each AppCard's
  // `app.icon` query resolves. Drives "search by Chinese / English app
  // name" and the `sort=label` ordering without forcing the whole page
  // to wait for icons. Cards that haven't been scrolled into view yet
  // simply contribute nothing here — by design, see the placeholder
  // hint below.
  const [labelIndex, setLabelIndex] = useState<
    Record<string, { label?: string; labelZh?: string }>
  >({});
  const handleLabelLoaded = (pkg: string, info: { label?: string; labelZh?: string }): void => {
    setLabelIndex((prev) => {
      const cur = prev[pkg];
      if (cur && cur.label === info.label && cur.labelZh === info.labelZh) return prev;
      return { ...prev, [pkg]: { label: info.label, labelZh: info.labelZh } };
    });
  };

  // Reset selection when the device or scope changes — keeping the
  // previous selection across scopes can re-add an item the user
  // never saw (e.g. they were viewing system apps, switched to
  // third-party, and the bulk-action bar still reflected the system
  // selection).
  useEffect(() => {
    setSelected(new Set());
    setDetailPkg(undefined);
  }, [serial, scope]);

  // Any filter/sort/scope change can shrink the result set; jumping
  // back to page 1 avoids landing on an empty page after a search.
  useEffect(() => {
    setPage(1);
  }, [serial, scope, query, sort, pageSize]);

  const listQuery = useQuery({
    queryKey: ['apps.list', serial, scope],
    queryFn: async () => {
      const args: Record<string, unknown> = {};
      if (scope === 'thirdParty') args.onlyThirdParty = true;
      if (scope === 'system') args.onlySystem = true;
      const out = await adbApi.run<{ items: AppEntry[] }>({
        serial,
        name: 'app.list.detailed',
        args,
      });
      return out.result.items;
    },
    enabled: !!serial && isOnline,
    staleTime: 5 * 60 * 1000,
  });

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = (listQuery.data ?? []).filter((it) => {
      if (!q) return true;
      if (it.package.toLowerCase().includes(q)) return true;
      const lbl = labelIndex[it.package];
      if (lbl?.label && lbl.label.toLowerCase().includes(q)) return true;
      if (lbl?.labelZh && lbl.labelZh.toLowerCase().includes(q)) return true;
      return false;
    });
    items.sort((a, b) => {
      switch (sort) {
        case 'label': {
          // Use the cached label / labelZh once the IntersectionObserver
          // has fanned out `app.icon` for that card; fall back to the
          // package name for cards that haven't loaded yet, which keeps
          // sort stable as labels stream in.
          const la =
            labelIndex[a.package]?.labelZh ?? labelIndex[a.package]?.label ?? a.package;
          const lb =
            labelIndex[b.package]?.labelZh ?? labelIndex[b.package]?.label ?? b.package;
          return la.localeCompare(lb, 'zh-Hans-CN');
        }
        case 'package':
          return a.package.localeCompare(b.package);
        case 'version':
          return (b.versionCode ?? 0) - (a.versionCode ?? 0);
      }
    });
    return items;
  }, [listQuery.data, query, sort, labelIndex]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageItems = useMemo(
    () => filteredSorted.slice(pageStart, pageStart + pageSize),
    [filteredSorted, pageStart, pageSize]
  );

  const toggle = (pkg: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pkg)) next.delete(pkg);
      else next.add(pkg);
      return next;
    });
  };

  const showToast = (msg: string): void => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  };

  /* ------------------------- single-app mutations ----------------- */

  const startMut = useMutation({
    mutationFn: (pkg: string) =>
      adbApi.run({ serial, name: 'app.start', args: { package: pkg } }),
    onSuccess: (_r, pkg) => showToast(`已启动 ${pkg}`),
    onError: (e: Error) => showToast(`启动失败: ${e.message}`),
  });
  const stopMut = useMutation({
    mutationFn: (pkg: string) =>
      adbApi.run({ serial, name: 'app.stop', args: { package: pkg } }),
    onSuccess: (_r, pkg) => showToast(`已强制停止 ${pkg}`),
    onError: (e: Error) => showToast(`停止失败: ${e.message}`),
  });
  const clearMut = useMutation({
    mutationFn: (pkg: string) =>
      adbApi.run({ serial, name: 'app.clear', args: { package: pkg } }),
    onSuccess: (_r, pkg) => {
      showToast(`已清除 ${pkg} 数据`);
      qc.invalidateQueries({ queryKey: ['apps.list', serial] });
    },
    // ROM_RESTRICTED_CLEAR fires on OPPO/Realme/Vivo/Honor where the
    // shell uid loses CLEAR_APP_USER_DATA — no amount of pm-flag
    // fiddling will fix it. The most useful next action is to land
    // the user on the system "App info" page so they can tap "Clear
    // data" themselves, which is exactly what `app.openSettings`
    // does. We do that pivot automatically and keep the toast to one
    // short line; if `app.openSettings` itself fails (device went
    // offline mid-action, etc.) we degrade to a manual-path hint so
    // the user isn't left guessing.
    onError: async (e: Error, pkg: string) => {
      if (!e.message.includes('ROM_RESTRICTED_CLEAR')) {
        showToast(`清除失败: ${e.message}`);
        return;
      }
      try {
        await adbApi.run({
          serial,
          name: 'app.openSettings',
          args: { package: pkg },
        });
        showToast(`该 ROM 禁止 shell 清数据，已在手机上打开 ${pkg} 设置页，请手动清除`);
      } catch (openErr) {
        showToast(
          `该 ROM 禁止 shell 清数据，且无法自动跳转设置页（${(openErr as Error).message}）。请在手机「设置 → 应用 → ${pkg}」手动操作`
        );
      }
    },
  });
  const uninstallMut = useMutation({
    mutationFn: (pkg: string) =>
      adbApi.run({ serial, name: 'app.uninstall', args: { package: pkg } }),
    onSuccess: (_r, pkg) => {
      showToast(`已卸载 ${pkg}`);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(pkg);
        return next;
      });
      qc.invalidateQueries({ queryKey: ['apps.list', serial] });
    },
    onError: (e: Error) => showToast(`卸载失败: ${e.message}`),
  });

  const pullApkMut = useMutation({
    mutationFn: async (entry: AppEntry) => {
      const out = await adbApi.run<PullApkResult>({
        serial,
        name: 'app.pullApk',
        args: { package: entry.package, apkPath: entry.apkPath, versionCode: entry.versionCode },
      });
      return out.result;
    },
    onSuccess: (r) => {
      showToast(`已导出 ${r.package} (${formatBytes(r.sizeBytes)})，正在下载…`);
      // Trigger an immediate browser download via /api/files/raw.
      const url = filesApi.rawUrl(r.hubPath, true);
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onError: (e: Error) => showToast(`导出失败: ${e.message}`),
  });

  const analyzeApkMut = useMutation({
    mutationFn: async (entry: AppEntry) => {
      const out = await adbApi.run<PullApkResult>({
        serial,
        name: 'app.pullApk',
        args: { package: entry.package, apkPath: entry.apkPath, versionCode: entry.versionCode },
      });
      return staticApkApi.analyze({ hubPath: out.result.hubPath, sourceDeviceSerial: serial });
    },
    onSuccess: (r) => {
      showToast('APK 分析完成，正在打开工作台…');
      navigate(`/static-analysis/apk/${r.reportId}`);
    },
    onError: (e: Error) => showToast(`分析失败: ${e.message}`),
  });

  /* ------------------------- render ------------------------------ */

  const empty = !listQuery.isLoading && filteredSorted.length === 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <AppWindow className="h-5 w-5 text-accent" />
            <h1 className="text-lg font-semibold text-ink">应用 · Apps</h1>
            {listQuery.data && (
              <span className="badge">{filteredSorted.length} / {listQuery.data.length}</span>
            )}
          </div>
          <p className="mt-1 text-[12px] text-ink-muted">
            管理设备上的应用：启动 / 强制停止 / 清除数据 / 卸载 / 导出 APK。
          </p>
        </div>
        <button
          className="btn-ghost"
          disabled={!serial || !isOnline || listQuery.isFetching}
          onClick={() => listQuery.refetch()}
          title="刷新列表"
        >
          <RefreshCw className={clsx('h-4 w-4', listQuery.isFetching && 'animate-spin')} />
          刷新
        </button>
      </header>

      {!serial && (
        <div className="card-pad text-sm text-ink-muted">
          请先在设备主页选择一个在线设备。Apps 页面的所有操作都依赖 selected serial。
        </div>
      )}

      {serial && !isOnline && (
        <div className="card-pad text-sm text-amber-600 dark:text-amber-400">
          当前设备处于离线 / 未授权状态，无法读取应用列表。检查 USB 调试授权或重新连接 WiFi。
        </div>
      )}

      {serial && isOnline && (
        <>
          <div className="card p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5">
                {(['thirdParty', 'system', 'all'] as Scope[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setScope(s)}
                    className={clsx(
                      'rounded-md border px-2.5 py-1 text-xs transition',
                      scope === s
                        ? 'border-accent bg-accent-soft text-accent'
                        : 'border-line text-ink-muted hover:bg-bg-soft hover:text-ink'
                    )}
                  >
                    {s === 'thirdParty' ? '三方' : s === 'system' ? '系统' : '全部'}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-dim" />
                <input
                  className="input w-full pl-7"
                  placeholder="搜索包名 / 应用名…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                <span>排序</span>
                <select
                  className="select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                >
                  <option value="label">名称 / 包名</option>
                  <option value="package">包名</option>
                  <option value="version">版本号 (新→旧)</option>
                </select>
              </label>
            </div>

            {selected.size > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3 text-xs">
                <span className="text-ink">
                  已选 <b className="text-accent">{selected.size}</b> 个应用
                </span>
                <button className="btn-ghost btn-xs" onClick={() => setSelected(new Set())}>
                  清空选择
                </button>
                <span className="ml-2 text-[11px] text-ink-dim">
                  逐个操作请在下方应用卡片上点击图标按钮。
                </span>
              </div>
            )}
          </div>

          {listQuery.isLoading && (
            <div className="card-pad text-center text-sm text-ink-muted">加载应用列表…</div>
          )}
          {listQuery.error && (
            <div className="card-pad text-center text-sm text-rose-600 dark:text-rose-400">
              加载失败：{(listQuery.error as Error).message}
            </div>
          )}
          {empty && (
            <div className="card-pad text-center text-sm text-ink-muted">
              没有匹配的应用。
              {query.trim() &&
                listQuery.data &&
                Object.keys(labelIndex).length < listQuery.data.length && (
                  <div className="mt-1 text-[11px] text-ink-dim">
                    应用名是滚动到可视区时才加载的。搜不到时可滚动列表 /
                    切到「全部」/ 等待图标加载完成（已加载 {Object.keys(labelIndex).length} /{' '}
                    {listQuery.data.length}）。
                  </div>
                )}
            </div>
          )}

          {!listQuery.isLoading && !empty && (
            <>
              {/*
                Auto-fill grid: row packs as many ~220px cards as the
                container can hold (≈5 cols at 1280px, 6 at 1536px,
                8+ on ultrawide), so the layout never feels sparse on
                large monitors but still degrades to 1-2 cols on
                phones. `minmax(220px, 1fr)` keeps each card from
                stretching too wide when the trailing row is short.
              */}
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
              >
                {pageItems.map((it) => (
                  <AppCard
                    key={it.package}
                    entry={it}
                    serial={serial}
                    selected={selected.has(it.package)}
                    busy={
                      (startMut.isPending && startMut.variables === it.package) ||
                      (stopMut.isPending && stopMut.variables === it.package) ||
                      (clearMut.isPending && clearMut.variables === it.package) ||
                      (uninstallMut.isPending && uninstallMut.variables === it.package) ||
                      (pullApkMut.isPending && pullApkMut.variables?.package === it.package) ||
                      (analyzeApkMut.isPending && analyzeApkMut.variables?.package === it.package)
                    }
                    onToggle={() => toggle(it.package)}
                    onOpenDetail={() => setDetailPkg(it.package)}
                    onPickIcon={(label) => {
                      setIconPickerLabel(label);
                      setIconPickerPkg(it);
                    }}
                    onLabelLoaded={handleLabelLoaded}
                    onStart={() => startMut.mutate(it.package)}
                    onStop={() => stopMut.mutate(it.package)}
                    onClear={() => setConfirm({ kind: 'clear', pkg: it.package })}
                    onUninstall={() => setConfirm({ kind: 'uninstall', pkg: it.package })}
                    onExportApk={() => pullApkMut.mutate(it)}
                    onAnalyzeApk={() => analyzeApkMut.mutate(it)}
                  />
                ))}
              </div>

              <Pagination
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filteredSorted.length}
                onPage={setPage}
                onPageSize={setPageSize}
              />
            </>
          )}
        </>
      )}

      {detailPkg && (
        <AppDetailDrawer
          serial={serial!}
          pkg={detailPkg}
          isSystem={
            listQuery.data?.find((it) => it.package === detailPkg)?.isSystem ?? false
          }
          onClose={() => setDetailPkg(undefined)}
          onToast={showToast}
        />
      )}

      {confirm && (
        <ConfirmModal
          kind={confirm.kind}
          pkg={confirm.pkg}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.kind === 'uninstall') uninstallMut.mutate(confirm.pkg);
            else clearMut.mutate(confirm.pkg);
            setConfirm(null);
          }}
        />
      )}

      {iconPickerPkg && serial && (
        <IconPickerModal
          serial={serial}
          package={iconPickerPkg.package}
          apkPath={iconPickerPkg.apkPath}
          versionCode={iconPickerPkg.versionCode}
          label={iconPickerLabel}
          onClose={() => {
            setIconPickerPkg(null);
            setIconPickerLabel(undefined);
          }}
          onToast={showToast}
        />
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-40 rounded-md border border-line bg-bg-card px-3 py-2 text-sm shadow-card">
          {toast}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Card                                                               */
/* ------------------------------------------------------------------ */

function AppCard({
  entry,
  serial,
  selected,
  busy,
  onToggle,
  onOpenDetail,
  onPickIcon,
  onLabelLoaded,
  onStart,
  onStop,
  onClear,
  onUninstall,
  onExportApk,
  onAnalyzeApk,
}: {
  entry: AppEntry;
  serial: string;
  selected: boolean;
  busy: boolean;
  onToggle: () => void;
  onOpenDetail: () => void;
  onPickIcon: (label?: string) => void;
  onLabelLoaded: (pkg: string, info: { label?: string; labelZh?: string }) => void;
  onStart: () => void;
  onStop: () => void;
  onClear: () => void;
  onUninstall: () => void;
  onExportApk: () => void;
  onAnalyzeApk: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  // Lazy icon load: keep the request shelf-stable across re-renders by
  // staying mounted once visible. Re-entering the viewport doesn't
  // re-trigger because we never flip back to `false`. Pagination
  // means the current page's 24 cards all get fetched once visible,
  // but cards on other pages never fetch at all.
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const iconQuery = useQuery({
    queryKey: ['app.icon', serial, entry.package, entry.versionCode],
    queryFn: async () => {
      const out = await adbApi.run<AppIconResult>({
        serial,
        name: 'app.icon',
        args: {
          package: entry.package,
          apkPath: entry.apkPath,
          versionCode: entry.versionCode,
        },
      });
      return out.result;
    },
    enabled: visible,
    // Icons are immutable per (package, versionCode) so cache forever.
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  // Bubble label / labelZh up to the page-level `labelIndex` so the
  // search box can match Chinese / English app names too. Only fires
  // when the query resolves (and the values actually change), so it
  // never spams the parent state on re-renders.
  useEffect(() => {
    if (!iconQuery.data) return;
    onLabelLoaded(entry.package, {
      label: iconQuery.data.label,
      labelZh: iconQuery.data.labelZh,
    });
  }, [iconQuery.data, entry.package, onLabelLoaded]);

  const display = iconQuery.data?.labelZh || iconQuery.data?.label || entry.package;
  const versionLabel = iconQuery.data?.versionName
    ? `v${iconQuery.data.versionName}${entry.versionCode ? ` (${entry.versionCode})` : ''}`
    : entry.versionCode
      ? `vc=${entry.versionCode}`
      : '';

  return (
    <div
      ref={ref}
      className={clsx(
        'card relative flex flex-col gap-2 p-2.5 text-sm transition',
        selected
          ? 'border-accent bg-accent-soft/30 ring-1 ring-accent/30'
          : 'hover:border-ink-dim/40 hover:shadow-md'
      )}
    >
      {/* Floating selector (top-left) + system tag (top-right) — keeps
          the main content row free for the icon + name so narrow cards
          (~220px) don't wrap the title onto a second line. */}
      <input
        type="checkbox"
        className="absolute left-2 top-2 h-4 w-4 cursor-pointer"
        checked={selected}
        onChange={onToggle}
        aria-label={`选择 ${entry.package}`}
      />
      {entry.isSystem && (
        <span className="badge absolute right-2 top-2 border-amber-500/30 text-amber-700 dark:text-amber-400">
          系统
        </span>
      )}

      <div className="mt-4 flex min-w-0 items-center gap-2.5">
        {/* Icon button — dedicated to opening the multi-icon picker
            modal (§3 of layout-name-apps-drop plan). Pulled out of the
            former "open detail" button so clicking the picture no
            longer triggers the drawer. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPickIcon(iconQuery.data?.labelZh || iconQuery.data?.label);
          }}
          className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-bg-soft transition hover:border-accent/60 hover:bg-accent-soft/30"
          title="查看 / 复制 / 下载 app 图标"
        >
          {iconQuery.data?.iconPng ? (
            <img
              src={`data:image/png;base64,${iconQuery.data.iconPng}`}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : iconQuery.isFetching ? (
            <Loader2 className="h-5 w-5 animate-spin text-ink-dim" />
          ) : (
            <AppWindow className="h-5 w-5 text-ink-dim" />
          )}
        </button>
        <button
          type="button"
          onClick={onOpenDetail}
          className="min-w-0 flex-1 text-left"
          title="查看详情"
        >
          <div className="truncate font-medium text-ink leading-tight" title={display}>
            {display}
          </div>
          <div
            className="mt-0.5 truncate font-mono text-[10.5px] leading-tight text-ink-dim"
            title={entry.package}
          >
            {entry.package}
          </div>
          {versionLabel && (
            <div className="mt-0.5 truncate text-[10.5px] leading-tight text-ink-muted">
              {versionLabel}
            </div>
          )}
        </button>
      </div>

      <div className="mt-auto flex items-center justify-between gap-0.5 border-t border-line pt-1.5">
        <button
          className="btn-ghost btn-xs"
          disabled={busy}
          onClick={onStart}
          title="启动"
        >
          <Play className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost btn-xs"
          disabled={busy}
          onClick={onStop}
          title="强制停止"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost btn-xs"
          disabled={busy}
          onClick={onClear}
          title="清除数据"
        >
          <Eraser className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost btn-xs"
          disabled={busy}
          onClick={onExportApk}
          title="导出 APK 到电脑"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost btn-xs"
          disabled={busy}
          onClick={onAnalyzeApk}
          title="导出并分析 APK"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost btn-xs text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
          disabled={busy || entry.isSystem}
          onClick={onUninstall}
          title={entry.isSystem ? '系统应用不允许卸载' : '卸载'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <button className="btn-ghost btn-xs" onClick={onOpenDetail} title="详情">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pagination                                                         */
/* ------------------------------------------------------------------ */

function Pagination({
  page,
  totalPages,
  pageSize,
  totalItems,
  onPage,
  onPageSize,
}: {
  page: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPage: (p: number) => void;
  onPageSize: (n: number) => void;
}) {
  if (totalItems === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  // Compact page-number rail: always show first & last, plus a window
  // of ±2 around the current page, with ellipsis for the gaps. Keeps
  // the bar short for 200-app catalogs (≈9 pages) and still navigable
  // for huge ROMs (system + third-party can easily hit 600+).
  const pages: Array<number | 'ellipsis'> = [];
  const push = (n: number): void => {
    if (n < 1 || n > totalPages) return;
    if (pages[pages.length - 1] === n) return;
    pages.push(n);
  };
  const windowed = new Set<number>([1, totalPages, page - 1, page, page + 1, page - 2, page + 2]);
  let prev = 0;
  for (let i = 1; i <= totalPages; i++) {
    if (!windowed.has(i)) continue;
    if (prev && i - prev > 1) pages.push('ellipsis');
    push(i);
    prev = i;
  }

  return (
    <div className="card flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
      <div className="text-ink-muted">
        显示 <b className="text-ink">{from}</b>–<b className="text-ink">{to}</b> /{' '}
        <b className="text-ink">{totalItems}</b>
      </div>
      <div className="flex items-center gap-1">
        <button
          className="btn-ghost btn-xs"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          上一页
        </button>
        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`e-${i}`} className="px-1 text-ink-dim">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p)}
              className={clsx(
                'min-w-[28px] rounded-md border px-1.5 py-0.5 transition',
                p === page
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-line text-ink-muted hover:bg-bg-soft hover:text-ink'
              )}
            >
              {p}
            </button>
          )
        )}
        <button
          className="btn-ghost btn-xs"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          下一页
        </button>
      </div>
      <label className="flex items-center gap-1.5 text-ink-muted">
        <span>每页</span>
        <select
          className="select"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
        >
          {[12, 24, 48, 96].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail drawer (tabbed: 概览 / 权限 / Activity)                    */
/* ------------------------------------------------------------------ */

function AppDetailDrawer({
  serial,
  pkg,
  isSystem,
  onClose,
  onToast,
}: {
  serial: string;
  pkg: string;
  isSystem: boolean;
  onClose: () => void;
  onToast: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DrawerTab>('overview');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [intentOpen, setIntentOpen] = useState(false);

  const [info, icon, storage, components, bundle, dexoptQ, processesQ, usersQ] = useQueries({
    queries: [
      {
        queryKey: ['app.info', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppInfoResult>({
            serial,
            name: 'app.info',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['app.icon', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppIconResult>({
            serial,
            name: 'app.icon',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: Infinity,
        retry: false,
      },
      {
        queryKey: ['app.storage', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppStorageResult>({
            serial,
            name: 'app.storage',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: 60 * 1000,
        retry: false,
      },
      {
        queryKey: ['app.components', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppComponentsResult>({
            serial,
            name: 'app.components',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: 5 * 60 * 1000,
        retry: false,
      },
      {
        queryKey: ['app.bundle', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppBundleResult>({
            serial,
            name: 'app.bundle',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: Infinity,
        retry: false,
      },
      {
        queryKey: ['app.dexopt', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppDexoptResult>({
            serial,
            name: 'app.dexopt',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: 5 * 60 * 1000,
        retry: false,
      },
      {
        queryKey: ['app.processes', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppProcessesResult>({
            serial,
            name: 'app.processes',
            args: { package: pkg },
          });
          return out.result;
        },
        // Refresh while the drawer's "运行" tab is open. Other tabs
        // happily reuse the snapshot — refetchInterval is gated by
        // `enabled` to keep idle drawers cheap.
        refetchInterval: tab === 'runtime' ? 5_000 : false,
        retry: false,
      },
      {
        queryKey: ['app.users', serial, pkg],
        queryFn: async () => {
          const out = await adbApi.run<AppUsersResult>({
            serial,
            name: 'app.users',
            args: { package: pkg },
          });
          return out.result;
        },
        staleTime: 5 * 60 * 1000,
        retry: false,
      },
    ],
  });

  // `app.netstats` needs the UID resolved by `app.info`. Keep it as its
  // own useQuery so the queryKey can include the UID — sharing one in
  // useQueries makes the dependency hard to express.
  const netstatsQ = useQuery({
    queryKey: ['app.netstats', serial, info.data?.uid ?? null],
    queryFn: async () => {
      const out = await adbApi.run<AppNetstatsResult>({
        serial,
        name: 'app.netstats',
        args: { package: pkg, ...(info.data?.uid !== undefined ? { uid: info.data.uid } : {}) },
      });
      return out.result;
    },
    enabled: !!info.data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const enabledState = info.data?.enabled;
  const isDisabled = enabledState === 'disabled' || enabledState === 'disabled-user';

  /* ---- mutations: enable/disable + intent + pullApks ----------------- */

  const enableMut = useMutation({
    mutationFn: async () => {
      await adbApi.run({
        serial,
        name: 'app.enable',
        args: { package: pkg, ...(userId !== undefined ? { userId } : {}) },
      });
    },
    onSuccess: () => {
      onToast(`已启用 ${pkg}`);
      qc.invalidateQueries({ queryKey: ['app.info', serial, pkg] });
      qc.invalidateQueries({ queryKey: ['apps.list', serial] });
    },
    onError: (e: Error) => onToast(`启用失败: ${e.message}`),
  });
  const disableMut = useMutation({
    mutationFn: async () => {
      await adbApi.run({
        serial,
        name: 'app.disable',
        args: { package: pkg, ...(userId !== undefined ? { userId } : {}) },
      });
    },
    onSuccess: () => {
      onToast(`已停用 ${pkg}`);
      qc.invalidateQueries({ queryKey: ['app.info', serial, pkg] });
      qc.invalidateQueries({ queryKey: ['apps.list', serial] });
    },
    onError: (e: Error) => onToast(`停用失败: ${e.message}`),
  });

  const pullApksMut = useMutation({
    mutationFn: async () => {
      const out = await adbApi.run<PullApksResult>({
        serial,
        name: 'app.pullApks',
        args: { package: pkg },
      });
      return out.result;
    },
    onSuccess: (r) => {
      onToast(`已导出 ${r.files.length} 个 APK 文件 (${formatBytes(r.totalBytes)})，开始下载…`);
      // Trigger one browser download per file. Most APK bundles top
      // out at 3-5 entries (base + abi + locale + dpi), so this stays
      // tractable; for true bulk we'd want a zip on the server side.
      for (const f of r.files) {
        const url = filesApi.rawUrl(f.hubPath, true);
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    },
    onError: (e: Error) => onToast(`导出失败: ${e.message}`),
  });

  const analyzeSplitsMut = useMutation({
    mutationFn: async () => {
      const out = await adbApi.run<PullApksResult>({
        serial,
        name: 'app.pullApks',
        args: { package: pkg },
      });
      if (out.result.files.length === 0) throw new Error('未导出任何 APK 文件');
      return staticApkApi.analyze({ hubPath: out.result.dirHubPath, sourceDeviceSerial: serial });
    },
    onSuccess: (r) => {
      onToast('APK 分析完成，正在打开工作台…');
      navigate(`/static-analysis/apk/${r.reportId}`);
    },
    onError: (e: Error) => onToast(`分析失败: ${e.message}`),
  });

  const label = icon.data?.labelZh || icon.data?.label || pkg;

  return (
    <div className="fixed inset-0 z-30 flex">
      <button
        type="button"
        className="flex-1 bg-black/30"
        aria-label="关闭"
        onClick={onClose}
      />
      <aside className="flex w-full max-w-md flex-col border-l border-line bg-bg-card shadow-card sm:w-[520px]">
        <header className="flex items-center justify-between gap-2 border-b border-line p-4">
          <div className="flex min-w-0 items-center gap-3">
            {icon.data?.iconPng ? (
              <img
                src={`data:image/png;base64,${icon.data.iconPng}`}
                alt=""
                className="h-12 w-12 rounded-lg border border-line"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg border border-line bg-bg-soft" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-base font-semibold">{label}</span>
                {isDisabled && (
                  <span className="badge border-rose-500/30 text-rose-600 dark:text-rose-400">
                    已停用
                  </span>
                )}
              </div>
              <div className="truncate font-mono text-[11px] text-ink-muted">{pkg}</div>
            </div>
          </div>
          <button className="btn-ghost btn-xs" onClick={onClose} title="关闭">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-1 border-b border-line px-3 py-2 text-xs">
          {DRAWER_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={clsx(
                'rounded-md border px-2.5 py-1 transition',
                tab === t
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-line text-ink-muted hover:bg-bg-soft hover:text-ink'
              )}
            >
              {DRAWER_TAB_LABEL[t]}
            </button>
          ))}
          <label
            id="app-drawer-userid"
            className="ml-auto flex items-center gap-1.5 text-ink-muted"
          >
            <span title="多用户 / 工作资料：留空走默认用户，0 / 10 / 11 / 999 等可手动指定">
              用户 ID
            </span>
            <input
              type="number"
              min={0}
              max={999}
              placeholder="默认"
              value={userId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setUserId(v === '' ? undefined : Number(v));
              }}
              className="input w-16 px-1.5 py-0.5 text-[11px]"
            />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'overview' && (
            <OverviewTab info={info} icon={icon} storage={storage} bundle={bundle} />
          )}
          {tab === 'components' && <ComponentsTab components={components} />}
          {tab === 'permissions' && (
            <PermissionsTab serial={serial} pkg={pkg} userId={userId} onToast={onToast} />
          )}
          {tab === 'signing' && (
            <SigningTab bundle={bundle} onToast={onToast} />
          )}
          {tab === 'runtime' && (
            <RuntimeTab
              processes={processesQ}
              netstats={netstatsQ}
              dexopt={dexoptQ}
              uidAvailable={info.data?.uid !== undefined}
            />
          )}
          {tab === 'users' && <UsersTab users={usersQ} />}
        </div>

        <footer className="grid grid-cols-2 gap-2 border-t border-line p-3 text-xs sm:grid-cols-3">
          <button
            className="btn-ghost"
            onClick={() => setIntentOpen(true)}
            title="以自定义 Intent 启动（深度链接 / QA 调试）"
          >
            <Send className="h-3.5 w-3.5" /> 以 Intent 启动…
          </button>
          {isDisabled ? (
            <button
              className="btn-ghost text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
              disabled={enableMut.isPending}
              onClick={() => enableMut.mutate()}
              title="pm enable"
            >
              <Power className="h-3.5 w-3.5" /> 启用
            </button>
          ) : (
            <button
              className="btn-ghost text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
              disabled={disableMut.isPending || isSystem === false}
              onClick={() => disableMut.mutate()}
              title={
                isSystem === false
                  ? '三方应用建议用「卸载」而非停用'
                  : 'pm disable-user (隐藏但不卸载)'
              }
            >
              <PowerOff className="h-3.5 w-3.5" /> 停用
            </button>
          )}
          <button
            className="btn-ghost"
            disabled={pullApksMut.isPending}
            onClick={() => pullApksMut.mutate()}
            title="导出 base.apk + 所有 split_config.*.apk"
          >
            {pullApksMut.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Package className="h-3.5 w-3.5" />
            )}
            导出全部 split APK
          </button>
          <button
            className="btn-ghost"
            disabled={analyzeSplitsMut.isPending}
            onClick={() => analyzeSplitsMut.mutate()}
            title="导出 base.apk + split_config.*.apk 后合并分析"
          >
            {analyzeSplitsMut.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Search className="h-3.5 w-3.5" />
            )}
            分析全部 split
          </button>
        </footer>
      </aside>

      {intentOpen && (
        <IntentLaunchDialog
          serial={serial}
          pkg={pkg}
          userId={userId}
          onClose={() => setIntentOpen(false)}
          onToast={onToast}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Drawer tabs                                                        */
/* ------------------------------------------------------------------ */

function OverviewTab({
  info,
  icon,
  storage,
  bundle,
}: {
  info: { data?: AppInfoResult; isLoading: boolean; error: unknown };
  icon: { data?: AppIconResult };
  storage: { data?: AppStorageResult; isLoading: boolean; error: unknown };
  bundle: { data?: AppBundleResult; isLoading: boolean; error: unknown };
}) {
  if (info.isLoading) return <div className="text-sm text-ink-muted">加载详情…</div>;
  if (info.error)
    return (
      <div className="text-sm text-rose-600 dark:text-rose-400">
        读取失败：{(info.error as Error).message}
      </div>
    );

  const flags = info.data?.flags;
  const manifest = bundle.data?.manifest;

  return (
    <div className="space-y-4">
      <Section title="身份" icon={<Info className="h-3.5 w-3.5" />}>
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[12px]">
          <Row label="包名">{info.data?.packageName ?? '-'}</Row>
          <Row label="UID">
            {info.data?.uid !== undefined ? (
              <span className="font-mono text-[11px]">{info.data.uid}</span>
            ) : (
              '-'
            )}
          </Row>
          {info.data?.sharedUserId && <Row label="sharedUserId">{info.data.sharedUserId}</Row>}
          <Row label="dataDir">
            <span className="break-all font-mono text-[11px]">{info.data?.dataDir ?? '-'}</span>
          </Row>
          <Row label="codePath">
            <span className="break-all font-mono text-[11px]">{info.data?.codePath ?? '-'}</span>
          </Row>
        </dl>
      </Section>

      <Section title="构建" icon={<Box className="h-3.5 w-3.5" />}>
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[12px]">
          <Row label="版本">
            {info.data?.versionName ?? manifest?.versionName ?? icon.data?.versionName ?? '?'}{' '}
            {info.data?.versionCode !== undefined && (
              <span className="text-ink-muted">({info.data.versionCode})</span>
            )}
          </Row>
          <Row label="minSdk / target / compile">
            <span className="font-mono">
              {info.data?.minSdk ?? '?'} / {info.data?.targetSdk ?? '?'} /{' '}
              {manifest?.compileSdkVersion ?? '?'}
              {manifest?.compileSdkVersionCodename
                ? ` (${manifest.compileSdkVersionCodename})`
                : ''}
            </span>
          </Row>
          <Row label="primary ABI">{info.data?.primaryCpuAbi ?? '-'}</Row>
          {info.data?.secondaryCpuAbi && (
            <Row label="secondary ABI">{info.data.secondaryCpuAbi}</Row>
          )}
          <Row label="APK 内 ABI">
            {manifest?.nativeAbis && manifest.nativeAbis.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {manifest.nativeAbis.map((a) => (
                  <span key={a} className="badge font-mono">
                    {a}
                  </span>
                ))}
              </span>
            ) : bundle.isLoading ? (
              <span className="text-ink-dim">解析中…</span>
            ) : (
              <span className="text-ink-dim">无 native lib</span>
            )}
          </Row>
          {info.data?.nativeLibraryDir && (
            <Row label="nativeLibraryDir">
              <span className="break-all font-mono text-[11px]">
                {info.data.nativeLibraryDir}
              </span>
            </Row>
          )}
        </dl>
      </Section>

      <Section title="安装" icon={<Download className="h-3.5 w-3.5" />}>
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[12px]">
          <Row label="启用状态">{info.data?.enabled ?? '-'}</Row>
          <Row label="installer">
            <span className="font-mono text-[11px]">
              {info.data?.installerPackageName ?? '-'}
            </span>
          </Row>
          {info.data?.installInitiatingPackageName && (
            <Row label="initiating">
              <span className="font-mono text-[11px]">
                {info.data.installInitiatingPackageName}
              </span>
            </Row>
          )}
          {info.data?.installOriginatingPackageName && (
            <Row label="originating">
              <span className="font-mono text-[11px]">
                {info.data.installOriginatingPackageName}
              </span>
            </Row>
          )}
          <Row label="首次安装">{info.data?.firstInstallTime ?? '-'}</Row>
          <Row label="最近更新">{info.data?.lastUpdateTime ?? '-'}</Row>
          {info.data?.isUpdatedSystemApp && (
            <Row label="updated system">
              <span className="badge border-amber-500/30 text-amber-700 dark:text-amber-400">
                系统 App 已被 OTA 更新覆盖
              </span>
            </Row>
          )}
          <Row label="抓取时间">{tsLocal(new Date().toISOString())}</Row>
        </dl>
      </Section>

      {flags && (
        <Section title="标志位" icon={<ShieldCheck className="h-3.5 w-3.5" />}>
          <div className="flex flex-wrap gap-1 text-[11px]">
            <FlagBadge active={flags.debuggable} tone="rose">
              debuggable
            </FlagBadge>
            <FlagBadge active={flags.testOnly} tone="amber">
              testOnly
            </FlagBadge>
            <FlagBadge active={flags.allowBackup} tone="neutral">
              allowBackup
            </FlagBadge>
            <FlagBadge active={flags.system} tone="neutral">
              system
            </FlagBadge>
            <FlagBadge active={flags.persistent} tone="neutral">
              persistent
            </FlagBadge>
          </div>
          {flags.debuggable && (
            <p className="mt-2 text-[10.5px] leading-snug text-rose-600 dark:text-rose-400">
              debuggable=true：允许 adb shell run-as / 注入调试器，正式发布版不应保留。
            </p>
          )}
        </Section>
      )}

      <Section title="存储占用" icon={<Layers className="h-3.5 w-3.5" />}>
        {storage.isLoading ? (
          <div className="text-[11px] text-ink-muted">读取中…</div>
        ) : storage.error ? (
          <div className="text-[11px] text-rose-600 dark:text-rose-400">
            {(storage.error as Error).message}
          </div>
        ) : (
          <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-[11px]">
            <Row label="APK">
              {storage.data ? formatBytes(storage.data.apkBytes) : '-'}
              {storage.data && storage.data.apkFiles > 1 && (
                <span className="ml-1 text-ink-muted">
                  ({storage.data.apkFiles} 个 split)
                </span>
              )}
            </Row>
            <Row label="数据">
              {storage.data?.dataBytes === null || storage.data?.dataBytes === undefined ? (
                <span className="text-ink-dim">读取受限</span>
              ) : (
                formatBytes(storage.data.dataBytes)
              )}
            </Row>
            <Row label="缓存">
              {storage.data?.cacheBytes === null || storage.data?.cacheBytes === undefined ? (
                <span className="text-ink-dim">读取受限</span>
              ) : (
                formatBytes(storage.data.cacheBytes)
              )}
            </Row>
          </dl>
        )}
        {storage.data?.note && (
          <p className="mt-2 text-[10.5px] leading-snug text-ink-muted">{storage.data.note}</p>
        )}
      </Section>

      {info.data?.raw && (
        <details className="text-[11px]">
          <summary className="cursor-pointer select-none text-ink-muted">
            <Info className="mr-1 inline h-3.5 w-3.5" /> 原始 dumpsys 字段（
            {Object.keys(info.data.raw).length} 项）
          </summary>
          <pre className="mt-2 max-h-[320px] overflow-auto rounded-md border border-line bg-bg-soft p-2 font-mono">
            {JSON.stringify(info.data.raw, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-ink">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

function FlagBadge({
  active,
  tone,
  children,
}: {
  active?: boolean;
  tone: 'rose' | 'amber' | 'neutral';
  children: React.ReactNode;
}) {
  if (!active) {
    return <span className="badge border-line text-ink-dim">{children}</span>;
  }
  const cls =
    tone === 'rose'
      ? 'border-rose-500/30 text-rose-600 dark:text-rose-400'
      : tone === 'amber'
        ? 'border-amber-500/30 text-amber-700 dark:text-amber-400'
        : 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400';
  return <span className={`badge ${cls}`}>{children}</span>;
}

function PermissionsTab({
  serial,
  pkg,
  userId,
  onToast,
}: {
  serial: string;
  pkg: string;
  userId: number | undefined;
  onToast: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const permsQuery = useQuery({
    queryKey: ['app.permissions', serial, pkg],
    queryFn: async () => {
      const out = await adbApi.run<AppPermissionsResult>({
        serial,
        name: 'app.permissions',
        args: { package: pkg },
      });
      return out.result;
    },
    staleTime: 60 * 1000,
  });

  const flipMut = useMutation({
    mutationFn: async ({
      permission,
      action,
    }: {
      permission: string;
      action: 'grant' | 'revoke';
    }) => {
      await adbApi.run({
        serial,
        name: `app.${action}`,
        args: {
          package: pkg,
          permission,
          ...(userId !== undefined ? { userId } : {}),
        },
      });
    },
    onSuccess: (_r, vars) => {
      onToast(`已${vars.action === 'grant' ? '授予' : '撤销'} ${vars.permission}`);
      qc.invalidateQueries({ queryKey: ['app.permissions', serial, pkg] });
    },
    onError: (e: Error) => onToast(`操作失败: ${e.message}`),
  });

  if (permsQuery.isLoading) return <div className="text-sm text-ink-muted">加载权限…</div>;
  if (permsQuery.error)
    return (
      <div className="text-sm text-rose-600 dark:text-rose-400">
        加载失败：{(permsQuery.error as Error).message}
      </div>
    );
  const data = permsQuery.data!;
  const runtimeNames = new Set(data.runtime.map((p) => p.name));
  const installOnly = data.install.filter((p) => !runtimeNames.has(p));
  const requestedOnly = data.requested.filter(
    (p) => !runtimeNames.has(p) && !data.install.includes(p)
  );

  return (
    <div className="space-y-4 text-[12px]">
      <section>
        <h3 className="mb-1 flex items-center gap-1.5 text-[12px] font-semibold text-ink">
          <ShieldCheck className="h-3.5 w-3.5" /> 运行时权限（dangerous）
          <span className="badge ml-1">{data.runtime.length}</span>
        </h3>
        {data.runtime.length === 0 ? (
          <p className="text-[11px] text-ink-muted">无运行时权限</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line">
            {data.runtime.map((p) => (
              <li
                key={p.name}
                className="flex items-center justify-between gap-2 px-2 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-[11px] text-ink" title={p.name}>
                    {permLabel(p.name)}
                  </div>
                  {p.flags && (
                    <div className="truncate text-[10px] text-ink-dim">{p.flags}</div>
                  )}
                </div>
                {p.granted ? (
                  <>
                    <span className="badge border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                      已授予
                    </span>
                    <button
                      className="btn-ghost btn-xs"
                      disabled={flipMut.isPending}
                      onClick={() => flipMut.mutate({ permission: p.name, action: 'revoke' })}
                    >
                      撤销
                    </button>
                  </>
                ) : (
                  <>
                    <span className="badge border-rose-500/30 text-rose-600 dark:text-rose-400">
                      未授予
                    </span>
                    <button
                      className="btn-ghost btn-xs"
                      disabled={flipMut.isPending}
                      onClick={() => flipMut.mutate({ permission: p.name, action: 'grant' })}
                    >
                      授予
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {installOnly.length > 0 && (
        <section>
          <h3 className="mb-1 text-[12px] font-semibold text-ink">
            安装期已自动授予 <span className="badge ml-1">{installOnly.length}</span>
          </h3>
          <ul className="rounded-md border border-line p-2">
            {installOnly.map((p) => (
              <li key={p} className="truncate font-mono text-[11px] text-ink-muted" title={p}>
                {permLabel(p)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {requestedOnly.length > 0 && (
        <section>
          <h3 className="mb-1 text-[12px] font-semibold text-ink">
            仅声明，未授予 <span className="badge ml-1">{requestedOnly.length}</span>
          </h3>
          <ul className="rounded-md border border-line p-2">
            {requestedOnly.map((p) => (
              <li key={p} className="truncate font-mono text-[11px] text-ink-dim" title={p}>
                {permLabel(p)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ComponentsTab({
  components,
}: {
  components: { data?: AppComponentsResult; isLoading: boolean; error: unknown };
}) {
  if (components.isLoading) return <div className="text-sm text-ink-muted">加载组件…</div>;
  if (components.error)
    return (
      <div className="text-sm text-rose-600 dark:text-rose-400">
        加载失败：{(components.error as Error).message}
      </div>
    );
  const data = components.data;
  if (!data) return <div className="text-sm text-ink-muted">无数据</div>;

  const totalExported =
    data.activities.filter((a) => a.exported).length +
    data.services.filter((a) => a.exported).length +
    data.receivers.filter((a) => a.exported).length +
    data.providers.filter((a) => a.exported).length;

  return (
    <div className="space-y-3 text-[12px]">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-muted">
        <span>
          activities=<b className="text-ink">{data.activities.length}</b>
        </span>
        <span>
          services=<b className="text-ink">{data.services.length}</b>
        </span>
        <span>
          receivers=<b className="text-ink">{data.receivers.length}</b>
        </span>
        <span>
          providers=<b className="text-ink">{data.providers.length}</b>
        </span>
        <span className="ml-auto">
          exported=<b className="text-rose-600 dark:text-rose-400">{totalExported}</b>
        </span>
      </div>

      {totalExported > 5 && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-[11px] text-rose-700 dark:text-rose-300">
          该应用对外暴露 {totalExported} 个组件，第三方进程可调用，注意安全。
        </div>
      )}

      <ComponentSection title="Activity" entries={data.activities} />
      <ComponentSection title="Service" entries={data.services} />
      <ComponentSection title="Receiver" entries={data.receivers} />
      <ProviderSection entries={data.providers} />
    </div>
  );
}

function ComponentSection({
  title,
  entries,
}: {
  title: string;
  entries: AppComponentEntry[];
}) {
  if (entries.length === 0) {
    return (
      <details className="rounded-md border border-line">
        <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] text-ink-muted">
          {title} <span className="ml-1 text-ink-dim">(0)</span>
        </summary>
      </details>
    );
  }
  return (
    <details className="rounded-md border border-line" open>
      <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] font-semibold text-ink">
        {title} <span className="ml-1 text-ink-muted">({entries.length})</span>
      </summary>
      <ul className="divide-y divide-line">
        {entries.map((it) => (
          <li key={it.name} className="px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              {it.exported && (
                <span className="badge border-rose-500/30 text-rose-600 dark:text-rose-400">
                  exported
                </span>
              )}
              {it.intentFilters
                .filter((f) => f.endsWith('.MAIN') || f.endsWith('.LAUNCHER') || f.endsWith('.VIEW'))
                .slice(0, 2)
                .map((f) => (
                  <span key={f} className="badge font-mono text-[10px]">
                    {f.split('.').pop()}
                  </span>
                ))}
            </div>
            <div className="break-all font-mono text-[11px] text-ink" title={it.name}>
              {it.name.replace(/^[^/]+\//, '')}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function ProviderSection({ entries }: { entries: AppProviderEntry[] }) {
  if (entries.length === 0) {
    return (
      <details className="rounded-md border border-line">
        <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] text-ink-muted">
          Provider <span className="ml-1 text-ink-dim">(0)</span>
        </summary>
      </details>
    );
  }
  return (
    <details className="rounded-md border border-line" open>
      <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] font-semibold text-ink">
        Provider <span className="ml-1 text-ink-muted">({entries.length})</span>
      </summary>
      <ul className="divide-y divide-line">
        {entries.map((it) => (
          <li key={it.name} className="px-2 py-1.5">
            <div className="break-all font-mono text-[11px] text-ink" title={it.name}>
              {it.name.replace(/^[^/]+\//, '')}
            </div>
            {it.authorities.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {it.authorities.map((a) => (
                  <span
                    key={a}
                    className="badge border-accent/40 font-mono text-[10px] text-accent"
                  >
                    {a}
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function SigningTab({
  bundle,
  onToast,
}: {
  bundle: { data?: AppBundleResult; isLoading: boolean; error: unknown; refetch: () => void };
  onToast: (msg: string) => void;
}) {
  if (bundle.isLoading)
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> 正在拉取 APK 解析签名（首次较慢）…
      </div>
    );
  if (bundle.error)
    return (
      <div className="text-sm text-rose-600 dark:text-rose-400">
        读取失败：{(bundle.error as Error).message}
      </div>
    );

  const data = bundle.data;
  const signing = data?.signing;
  const signingMissing = data?.signingMissing;

  const copy = (label: string, text?: string): void => {
    if (!text) return;
    void navigator.clipboard.writeText(text).then(
      () => onToast(`已复制 ${label}`),
      () => onToast('复制失败：剪贴板权限被拒绝')
    );
  };

  return (
    <div className="space-y-4 text-[12px]">
      {signing?.isDebugLike && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-300">
          检测到调试签名 (CN=Android Debug)，正式发布版不应使用。
        </div>
      )}

      {signingMissing && (
        <div className="space-y-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-[11px] text-rose-700 dark:text-rose-300">
          <div className="flex items-start gap-2">
            <span className="flex-1">未读取到签名信息：hub 主机上没有找到 apksigner。</span>
            <button
              className="btn-ghost btn-xs shrink-0"
              onClick={() => bundle.refetch()}
            >
              <RefreshCw className="h-3.5 w-3.5" /> 重试
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Link
              to="/settings"
              className="btn-primary btn-xs h-7 text-[11px]"
              title="到「设置」页一次性配置 Android SDK 根目录"
            >
              <SettingsIcon className="h-3.5 w-3.5" /> 前往设置
            </Link>
            <span className="text-[10px] text-ink-dim">
              一处配置 SDK 根目录 → apksigner / aapt2 / apkanalyzer 一起恢复
            </span>
          </div>
          <details className="text-[10.5px] leading-snug text-ink-muted dark:text-ink-muted">
            <summary className="cursor-pointer select-none">手动修复（环境变量 / PATH）</summary>
            <ol className="ml-4 mt-0.5 list-decimal space-y-0.5">
              <li>
                设置环境变量 <code className="font-mono">ADH_APKSIGNER_PATH</code> 指到具体的
                <code className="font-mono"> apksigner </code>
                可执行文件，例如{' '}
                <code className="font-mono">
                  ~/Library/Android/sdk/build-tools/&lt;version&gt;/apksigner
                </code>
                ，重启 hub 即生效。
              </li>
              <li>
                或者把
                <code className="font-mono"> Android SDK build-tools </code>
                所在目录加到
                <code className="font-mono"> $PATH </code>
                里。
              </li>
              <li>
                或者设置 <code className="font-mono">ANDROID_HOME</code> /{' '}
                <code className="font-mono">ANDROID_SDK_ROOT</code> 指向 Android SDK 根目录，hub
                会自动从
                <code className="font-mono"> build-tools/&lt;最新版本&gt;/ </code>下挑 apksigner。
              </li>
            </ol>
          </details>
          {bundle.data?.signingDiagnostics && bundle.data.signingDiagnostics.triedPaths.length > 0 && (
            <details className="text-[10.5px] text-ink-muted">
              <summary className="cursor-pointer select-none">
                已查找过的路径（{bundle.data.signingDiagnostics.triedPaths.length}）
              </summary>
              <ul className="mt-1 max-h-32 overflow-y-auto rounded-sm bg-bg-soft p-1 font-mono">
                {bundle.data.signingDiagnostics.triedPaths.map((p) => (
                  <li key={p} className="break-all">
                    {p}
                  </li>
                ))}
              </ul>
              {bundle.data.signingDiagnostics.sdkRoots.length > 0 && (
                <div className="mt-1 text-[10px] text-ink-dim">
                  探测到的 SDK 根：{bundle.data.signingDiagnostics.sdkRoots.join(', ')}
                </div>
              )}
            </details>
          )}
        </div>
      )}

      <Section title="证书签名 / Certificate" icon={<Fingerprint className="h-3.5 w-3.5" />}>
        <div className="mb-2 flex flex-wrap items-center gap-1">
          <span className="text-[10.5px] text-ink-muted mr-1">scheme:</span>
          {(['v1', 'v2', 'v3', 'v4'] as const).map((s) => {
            const on = signing?.schemes.includes(s);
            return (
              <span
                key={s}
                className={clsx(
                  'badge font-mono',
                  on
                    ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                    : 'border-line text-ink-dim'
                )}
              >
                {s}
              </span>
            );
          })}
        </div>
        <DigestRow label="SHA-1" value={signing?.certificateSha1} onCopy={copy} />
        <DigestRow label="SHA-256" value={signing?.certificateSha256} onCopy={copy} />
        <DigestRow label="MD5" value={signing?.certificateMd5} onCopy={copy} />
        <dl className="mt-2 grid grid-cols-[60px_1fr] gap-x-3 gap-y-1.5 text-[11px]">
          <Row label="subject">
            <span className="break-all font-mono">{signing?.subject ?? '-'}</span>
          </Row>
          <Row label="issuer">
            <span className="break-all font-mono">{signing?.issuer ?? '-'}</span>
          </Row>
        </dl>
      </Section>

      <Section title="APK 文件哈希 / APK File Digest" icon={<Box className="h-3.5 w-3.5" />}>
        <p className="mb-2 text-[10.5px] text-ink-muted">
          整个 APK 文件的字节哈希，可用于反作弊 / 完整性比对（与上面的证书哈希用途不同）。
        </p>
        <DigestRow label="SHA-1" value={data?.apkSha1} onCopy={copy} />
        <DigestRow label="SHA-256" value={data?.apkSha256} onCopy={copy} />
        <DigestRow label="MD5" value={data?.apkMd5} onCopy={copy} />
        <dl className="mt-2 grid grid-cols-[60px_1fr] gap-x-3 gap-y-1.5 text-[11px]">
          <Row label="size">
            {data?.apkBytes !== undefined ? formatBytes(data.apkBytes) : '-'}
          </Row>
        </dl>
      </Section>
    </div>
  );
}

function DigestRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value?: string;
  onCopy: (label: string, text?: string) => void;
}) {
  const formatted = value ? value.replace(/(.{2})/g, '$1:').replace(/:$/, '') : undefined;
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="w-[60px] shrink-0 text-[11px] text-ink-muted">{label}</span>
      <code
        className="min-w-0 flex-1 break-all font-mono text-[11px] text-ink"
        title={value}
      >
        {formatted ?? '-'}
      </code>
      <button
        className="btn-ghost btn-xs"
        disabled={!value}
        onClick={() => onCopy(label, value)}
        title="复制 (无冒号)"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function RuntimeTab({
  processes,
  netstats,
  dexopt,
  uidAvailable,
}: {
  processes: { data?: AppProcessesResult; isLoading: boolean; error: unknown };
  netstats: { data?: AppNetstatsResult; isLoading: boolean; error: unknown };
  dexopt: { data?: AppDexoptResult; isLoading: boolean; error: unknown };
  uidAvailable: boolean;
}) {
  return (
    <div className="space-y-4 text-[12px]">
      <Section title="进程" icon={<Cpu className="h-3.5 w-3.5" />}>
        {processes.isLoading ? (
          <div className="text-[11px] text-ink-muted">读取中…</div>
        ) : processes.error ? (
          <div className="text-[11px] text-rose-600 dark:text-rose-400">
            {(processes.error as Error).message}
          </div>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
              {processes.data?.foreground ? (
                <span className="badge border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="mr-1 inline h-3 w-3" /> 前台
                </span>
              ) : (
                <span className="badge border-line text-ink-dim">非前台</span>
              )}
              {processes.data?.foregroundActivity && (
                <span className="font-mono text-[10.5px] text-ink-muted">
                  {processes.data.foregroundActivity}
                </span>
              )}
            </div>
            {processes.data?.processes.length === 0 ? (
              <p className="text-[11px] text-ink-muted">没有运行中的进程</p>
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line">
                {processes.data?.processes.map((p) => (
                  <li
                    key={`${p.processName}-${p.pid ?? 'no-pid'}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-[11px] text-ink">
                        {p.processName}
                      </div>
                      <div className="text-[10.5px] text-ink-dim">
                        pid={p.pid ?? '?'} · state={p.state ?? '?'}
                      </div>
                    </div>
                    <div className="text-[11px] text-ink-muted">
                      {p.rssKb !== undefined ? `${(p.rssKb / 1024).toFixed(1)} MB` : '-'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Section>

      <Section title="流量" icon={<Network className="h-3.5 w-3.5" />}>
        {!uidAvailable ? (
          <p className="text-[11px] text-ink-muted">等待 UID 解析…</p>
        ) : netstats.isLoading ? (
          <p className="text-[11px] text-ink-muted">读取中…</p>
        ) : netstats.error ? (
          <p className="text-[11px] text-rose-600 dark:text-rose-400">
            {(netstats.error as Error).message}
          </p>
        ) : !netstats.data?.available ? (
          <p className="text-[11px] text-ink-muted">
            {netstats.data?.warning ?? 'netstats 不可用'}
          </p>
        ) : (
          <div className="space-y-2">
            {netstats.data.windows.map((w) => (
              <div key={w.windowHours} className="rounded-md border border-line p-2">
                <div className="mb-1 text-[11px] font-semibold text-ink">
                  最近 {w.windowHours >= 24 ? `${Math.round(w.windowHours / 24)} 天` : `${w.windowHours} 小时`}
                </div>
                <dl className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-1 text-[11px]">
                  <Row label="mobile">
                    {w.mobile
                      ? `↓ ${formatBytes(w.mobile.rxBytes)} / ↑ ${formatBytes(w.mobile.txBytes)}`
                      : <span className="text-ink-dim">无</span>}
                  </Row>
                  <Row label="wifi">
                    {w.wifi
                      ? `↓ ${formatBytes(w.wifi.rxBytes)} / ↑ ${formatBytes(w.wifi.txBytes)}`
                      : <span className="text-ink-dim">无</span>}
                  </Row>
                  <Row label="total">
                    {w.total
                      ? `↓ ${formatBytes(w.total.rxBytes)} / ↑ ${formatBytes(w.total.txBytes)}`
                      : <span className="text-ink-dim">无</span>}
                  </Row>
                </dl>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Dexopt" icon={<Activity className="h-3.5 w-3.5" />}>
        {dexopt.isLoading ? (
          <p className="text-[11px] text-ink-muted">读取中…</p>
        ) : dexopt.error ? (
          <p className="text-[11px] text-rose-600 dark:text-rose-400">
            {(dexopt.error as Error).message}
          </p>
        ) : !dexopt.data?.ready ? (
          <p className="text-[11px] text-ink-muted">未读取到 dexopt 状态</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line">
            {dexopt.data.arches.map((a) => (
              <li
                key={`${a.isa}-${a.status}`}
                className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px]"
              >
                <span className="font-mono text-ink">{a.isa}</span>
                <span
                  className={clsx(
                    'badge font-mono',
                    a.status === 'speed-profile' || a.status === 'speed'
                      ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                      : a.status === 'verify' || a.status === 'extract' || a.status === 'run-from-apk'
                        ? 'border-amber-500/30 text-amber-700 dark:text-amber-400'
                        : 'border-line text-ink-muted'
                  )}
                >
                  {a.status}
                </span>
                <span className="ml-auto text-ink-muted">{a.reason ?? '-'}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function UsersTab({
  users,
}: {
  users: { data?: AppUsersResult; isLoading: boolean; error: unknown };
}) {
  if (users.isLoading) return <div className="text-sm text-ink-muted">读取中…</div>;
  if (users.error)
    return (
      <div className="text-sm text-rose-600 dark:text-rose-400">
        加载失败：{(users.error as Error).message}
      </div>
    );
  const list = users.data?.users ?? [];
  if (list.length === 0) {
    return <div className="text-sm text-ink-muted">未读取到任何 Android user</div>;
  }
  const onlyPrimary = list.length === 1 && list[0].userId === 0;

  return (
    <div className="space-y-3 text-[12px]">
      {onlyPrimary && (
        <div className="rounded-md border border-line bg-bg-soft p-2 text-[11px] text-ink-muted">
          设备未启用多用户 / 工作资料 / 双开。
        </div>
      )}
      <ul className="divide-y divide-line rounded-md border border-line">
        {list.map((u) => (
          <li key={u.userId} className="px-2 py-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="badge border-line font-mono">user {u.userId}</span>
              <span className="font-medium text-ink">{u.userName}</span>
              <UserTypeBadge type={u.userType} />
              {u.running && (
                <span className="badge border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  running
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
              {u.installed ? (
                <span className="badge border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  已安装
                </span>
              ) : (
                <span className="badge border-line text-ink-dim">未安装</span>
              )}
              {u.installed && u.enabled && (
                <span
                  className={clsx(
                    'badge font-mono',
                    u.enabled === 'enabled'
                      ? 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                      : 'border-rose-500/30 text-rose-600 dark:text-rose-400'
                  )}
                >
                  {u.enabled}
                </span>
              )}
              {u.hidden && (
                <span className="badge border-amber-500/30 text-amber-700 dark:text-amber-400">
                  hidden
                </span>
              )}
              {u.suspended && (
                <span className="badge border-amber-500/30 text-amber-700 dark:text-amber-400">
                  suspended
                </span>
              )}
              {u.stopped && (
                <span className="badge border-line text-ink-dim">stopped</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UserTypeBadge({ type }: { type: AppUserEntry['userType'] }) {
  const cls =
    type === 'work'
      ? 'border-violet-500/30 text-violet-600 dark:text-violet-400'
      : type === 'twin'
        ? 'border-pink-500/30 text-pink-600 dark:text-pink-400'
        : type === 'guest'
          ? 'border-amber-500/30 text-amber-700 dark:text-amber-400'
          : 'border-line text-ink-muted';
  return <span className={`badge ${cls}`}>{type}</span>;
}

/** Short permission label: `android.permission.CAMERA` → `CAMERA` (full name kept in `title`). */
function permLabel(full: string): string {
  const i = full.lastIndexOf('.');
  return i >= 0 ? full.slice(i + 1) : full;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="break-all text-ink">{children}</dd>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Intent launch dialog                                               */
/* ------------------------------------------------------------------ */

function IntentLaunchDialog({
  serial,
  pkg,
  userId,
  onClose,
  onToast,
}: {
  serial: string;
  pkg: string;
  userId: number | undefined;
  onClose: () => void;
  onToast: (msg: string) => void;
}) {
  const [action, setAction] = useState('android.intent.action.VIEW');
  const [data, setData] = useState('');
  const [component, setComponent] = useState('');
  const [category, setCategory] = useState('');
  // One `key=value` per line; lines starting with `#` or empty are ignored.
  // Inferred types: `bool:` / `int:` / `float:` prefixes give the backend a
  // hint via the typed extras map; everything else stays string.
  const [extrasRaw, setExtrasRaw] = useState('');

  const submitMut = useMutation({
    mutationFn: async () => {
      const extras = parseExtrasInput(extrasRaw);
      const args: Record<string, unknown> = {};
      if (action.trim()) args.action = action.trim();
      if (data.trim()) args.data = data.trim();
      if (component.trim()) args.component = component.trim();
      if (category.trim()) args.category = category.trim();
      if (Object.keys(extras).length > 0) args.extras = extras;
      // Always anchor to the current package so the intent doesn't
      // accidentally resolve to another app on the device.
      args.package = pkg;
      if (userId !== undefined) args.userId = userId;
      const out = await adbApi.run<{ ok: true; raw: string }>({
        serial,
        name: 'app.startIntent',
        args,
      });
      return out.result;
    },
    onSuccess: () => {
      onToast('Intent 已发送');
      onClose();
    },
    onError: (e: Error) => onToast(`Intent 启动失败: ${e.message}`),
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-line bg-bg-card p-4 shadow-card">
        <header className="mb-3 flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-accent" />
          <h2 className="text-sm font-semibold">以 Intent 启动 · {pkg}</h2>
        </header>
        <div className="space-y-2 text-[12px]">
          <Field label="action">
            <input
              className="input w-full"
              placeholder="android.intent.action.VIEW"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            />
          </Field>
          <Field label="data (URI)">
            <input
              className="input w-full"
              placeholder="https://example.com"
              value={data}
              onChange={(e) => setData(e.target.value)}
            />
          </Field>
          <Field label="component">
            <input
              className="input w-full"
              placeholder="com.example/.MainActivity"
              value={component}
              onChange={(e) => setComponent(e.target.value)}
            />
          </Field>
          <Field label="category">
            <input
              className="input w-full"
              placeholder="android.intent.category.DEFAULT"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </Field>
          <Field
            label="extras"
            hint="每行一个 key=value；可用 bool: / int: / float: 前缀强制类型，比如 isDebug=bool:true"
          >
            <textarea
              className="input min-h-[80px] w-full font-mono text-[11px]"
              placeholder={`foo=bar\nretries=int:3\nverbose=bool:true`}
              value={extrasRaw}
              onChange={(e) => setExtrasRaw(e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            取消
          </button>
          <button
            className="btn-primary"
            disabled={submitMut.isPending || (!action.trim() && !component.trim())}
            onClick={() => submitMut.mutate()}
          >
            {submitMut.isPending ? '提交中…' : '发送 Intent'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-0.5 text-[11px] text-ink-muted">{label}</div>
      {children}
      {hint && <div className="mt-0.5 text-[10.5px] text-ink-dim">{hint}</div>}
    </label>
  );
}

/** Parse the dialog's extras textarea. */
function parseExtrasInput(input: string): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const rawLine of input.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const eq = line.indexOf('=');
    if (eq < 0) continue;
    const key = line.slice(0, eq).trim();
    const val = line.slice(eq + 1).trim();
    if (!key) continue;
    // Typed prefixes — anything else stays a string.
    if (val.startsWith('bool:')) {
      out[key] = val.slice(5).toLowerCase() === 'true';
    } else if (val.startsWith('int:')) {
      const n = Number(val.slice(4));
      if (Number.isInteger(n)) out[key] = n;
    } else if (val.startsWith('float:')) {
      const n = Number(val.slice(6));
      if (Number.isFinite(n)) out[key] = n;
    } else {
      out[key] = val;
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Confirm modal                                                      */
/* ------------------------------------------------------------------ */

function ConfirmModal({
  kind,
  pkg,
  onCancel,
  onConfirm,
}: {
  kind: 'uninstall' | 'clear';
  pkg: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const title = kind === 'uninstall' ? '卸载应用' : '清除应用数据';
  const detail =
    kind === 'uninstall'
      ? `卸载 ${pkg} 后将不可恢复（除非重新安装）。继续？`
      : `清除 ${pkg} 的所有用户数据后将丢失登录态、设置、缓存等。继续？`;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-xl border border-line bg-bg-card p-4 shadow-card">
        <header className="mb-2 flex items-center gap-2">
          <Layers className="h-4 w-4 text-rose-500" />
          <h2 className="text-sm font-semibold">{title}</h2>
        </header>
        <p className="mb-4 text-[12px] text-ink-muted">{detail}</p>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel}>
            取消
          </button>
          <button
            className="btn-primary border-rose-500 bg-rose-500 hover:bg-rose-500/90"
            onClick={onConfirm}
          >
            {kind === 'uninstall' ? '卸载' : '清除数据'}
          </button>
        </div>
      </div>
    </div>
  );
}
